import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Tag, Database, Layers, Filter, ExternalLink } from 'lucide-react';
import { DataTable, Column } from '../../shared/DataTable';
import { StatCard } from '../../../shared/StatCard';
import { Badge } from '../../../../components/ui/Badge';
import { Select } from '../../../../components/ui/Select';
import { type Question, QUESTIONS_BY_FAMILY } from '../../../../content';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
// Subject metadata — derived from canonical source
const SUBJECT_META = ADMIN_MODULE_META;
interface FamilyRow {
  id: string;
  familyId: string;
  subjectCode: string;
  subjectName: string;
  questionCount: number;
  tierMin: number;
  tierMax: number;
  tierRange: string;
  types: string[];
  typesDisplay: string;
}
interface FamiliesTabProps {
  questions: Question[];
  onSelectFamily: (familyId: string) => void;
}
export function FamiliesTab({
  questions,
  onSelectFamily
}: FamiliesTabProps) {
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  // Build family rows from QUESTIONS_BY_FAMILY
  const familyRows = useMemo(() => {
    const rows: FamilyRow[] = [];
    Array.from(QUESTIONS_BY_FAMILY.entries()).forEach(([familyId, familyQuestions]) => {
      const code = familyId.split('-')[0] || 'UNK';
      const meta = SUBJECT_META[code];
      const tiers = familyQuestions.map(q => q.tier).filter(Boolean) as number[];
      const types = new Set<string>();
      familyQuestions.forEach(q => {
        if (q.questionType) types.add(q.questionType);
      });
      const tierMin = tiers.length > 0 ? Math.min(...tiers) : 0;
      const tierMax = tiers.length > 0 ? Math.max(...tiers) : 0;
      const tierRange = tierMin === tierMax ? `${tierMin}` : `${tierMin}–${tierMax}`;
      const typesArray = Array.from(types);
      const typesDisplay = typesArray.length <= 2 ? typesArray.join(', ') : `${typesArray.slice(0, 2).join(', ')} +${typesArray.length - 2}`;
      rows.push({
        id: familyId,
        familyId,
        subjectCode: code,
        subjectName: meta?.name || code,
        questionCount: familyQuestions.length,
        tierMin,
        tierMax,
        tierRange,
        types: typesArray,
        typesDisplay
      });
    });
    // Sort by familyId
    return rows.sort((a, b) => a.familyId.localeCompare(b.familyId));
  }, []);
  // Filter by subject
  const filteredRows = useMemo(() => {
    if (subjectFilter === 'all') return familyRows;
    return familyRows.filter(row => row.subjectCode === subjectFilter);
  }, [familyRows, subjectFilter]);
  // Stats
  const totalFamilies = QUESTIONS_BY_FAMILY.size;
  const subjectCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    familyRows.forEach(row => {
      counts[row.subjectCode] = (counts[row.subjectCode] || 0) + 1;
    });
    return counts;
  }, [familyRows]);
  const avgQuestionsPerFamily = useMemo(() => {
    if (totalFamilies === 0) return 0;
    return Math.round(questions.length / totalFamilies);
  }, [questions.length, totalFamilies]);
  // Table columns
  const columns: Column<FamilyRow>[] = [{
    key: 'subjectCode',
    label: 'Vak',
    width: '100px',
    render: (_, row) => <div className="flex items-center gap-2" data-id="element-5967" data-mp="etcxg85l9u08">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${SUBJECT_META[row.subjectCode]?.color || 'bg-gray-400'}`} data-id="element-5968" data-mp="iv7wf601rvau" />
          <span className="text-xs font-medium text-gray-600" data-id="element-5969" data-mp="nmpzq0pjm7yz">
            {row.subjectCode}
          </span>
        </div>
  }, {
    key: 'familyId',
    label: 'Family ID',
    render: value => <code className="text-sm font-mono font-semibold text-gray-800" data-id="element-5970" data-mp="xi9l3t6hdo9h">
          {value}
        </code>
  }, {
    key: 'questionCount',
    label: 'Vragen',
    width: '80px',
    render: value => <span className="text-sm font-semibold text-gray-700" data-id="element-5971" data-mp="1uc0tbqktv792">{value}</span>
  }, {
    key: 'tierRange',
    label: 'Tier',
    width: '80px',
    render: value => <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-id="element-5972" data-mp="241kcyygd754z">
          {value || '—'}
        </span>
  }, {
    key: 'typesDisplay',
    label: 'Typen',
    render: (_, row) => <div className="flex flex-wrap gap-1" data-id="element-5973" data-mp="1bdwym15otaqs">
          {row.types.slice(0, 2).map(type => <Badge key={type} variant="neutral" size="sm" data-id="element-5974" data-mp="1pdplfle3f6th">
              {type.replace(/_/g, ' ')}
            </Badge>)}
          {row.types.length > 2 && <Badge variant="neutral" size="sm" data-id="element-5975" data-mp="30rjmow8949p5">
              +{row.types.length - 2}
            </Badge>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-5976" data-mp="2wpoq2519mkhp">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-5977" data-mp="3497nhgb9ag9t">
        <motion.div variants={itemVariants} data-id="element-5978" data-mp="2ubkwhvgomy9h">
          <StatCard label="Totaal Families" value={totalFamilies} icon={<Tag className="w-5 h-5" data-id="element-5980" data-mp="2zbfjvp1915ps" />} color="purple" data-id="element-5979" data-mp="2ucuu6jjmi4fl" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5981" data-mp="3n1jnhweft5gs">
          <StatCard label="Vakken" value={Object.keys(SUBJECT_META).length} icon={<Database className="w-5 h-5" data-id="element-5983" data-mp="3daf8iworhkly" />} color="blue" data-id="element-5982" data-mp="3n2l2ud2hke4b" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5984" data-mp="3i9icsewx3msv">
          <StatCard label="Gem. Vragen/Family" value={avgQuestionsPerFamily} icon={<Layers className="w-5 h-5" data-id="element-5986" data-mp="9s5jijg52ies" />} color="emerald" data-id="element-5985" data-mp="3i87bclqy2r4x" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5987" data-mp="elzxd692c35y">
          <StatCard label="Totaal Vragen" value={questions.length.toLocaleString('nl-NL')} icon={<Database className="w-5 h-5" data-id="element-5989" data-mp="jh2p5lq6qzec" />} color="amber" data-id="element-5988" data-mp="og5hc5l2hb4w" />
        </motion.div>
      </motion.div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-5990" data-mp="fhtu8djesvh7">
        <div className="flex items-center gap-3" data-id="element-5991" data-mp="5ht70gif5v7q">
          <Filter className="w-4 h-4 text-gray-400" data-id="element-5992" data-mp="5jmhy802f1lj" />
          <Select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)} options={[{
          value: 'all',
          label: `Alle vakken (${totalFamilies})`
        }, ...Object.entries(SUBJECT_META).map(([code, meta]) => ({
          value: code,
          label: `${meta.name} (${subjectCounts[code] || 0})`
        }))]} size="sm" fullWidth={false} data-id="element-5993" data-mp="ps0zcbuh0q5" />
        </div>

        {subjectFilter !== 'all' && <div className="text-sm text-gray-500" data-id="element-5994" data-mp="1ydvu7e8yzckq">
            {filteredRows.length} van {totalFamilies} families
          </div>}
      </div>

      {/* Data Table */}
      <DataTable columns={columns} data={filteredRows} onRowClick={row => onSelectFamily(row.familyId)} searchable searchPlaceholder="Zoek op family ID..." searchKeys={['familyId', 'subjectCode', 'subjectName']} emptyMessage="Geen families gevonden" rowActions={[{
      label: 'Bekijk vragen',
      icon: ExternalLink,
      onClick: row => onSelectFamily(row.familyId)
    }]} data-id="element-5995" data-mp="23bnnablcp4qi" />
    </div>;
}